import {
  MOCK_DATA_MODEL_FIELDS_RESPONSE,
  MOCK_VALUE_TYPE_BY_CATEGORY,
} from "../chartStudio/chartStudioDataModel.mock";
import { mockValidationError } from "../mockDomainError";
import type { FieldRefApi } from "./storyCanvas.types";
import type {
  FieldHierarchyRefApi,
  FilterComparator,
  FilterScalarApi,
  FilterValueType,
} from "./storyFilters.types";
import type { DataModelFieldCategory } from "../../components/registry/dataModelFieldSections";
import { storyFilterComparators, storyFilterListLimit } from "../../components/registry/storyFilterComparators";
import { isScalarAfter } from "./filterRangeMappers";

/**
 * The backend's create rules for `/filters`, as pure functions so the mock
 * store and its tests share them. Each failure is the 422 the live API sends.
 */

export interface MockCatalogField {
  id: string;
  name: string;
  kind: FieldRefApi["kind"];
  category: DataModelFieldCategory;
  valueType: FilterValueType;
}

const GEOGRAPHY = { id: "hier-geography", name: "Geography", levels_total: 3 };
/** Year 1, Quarter 2, Month 3, Day 4; the mock's `order_date` holds month starts, like the real "Order Month". */
const CALENDAR = { id: "hier_date", name: "Calendar", levels_total: 4 };
const HIERARCHY_BY_FIELD_ID: Record<string, FieldHierarchyRefApi> = {
  region: { ...GEOGRAPHY, level_no: 1 },
  state: { ...GEOGRAPHY, level_no: 2 },
  city: { ...GEOGRAPHY, level_no: 3 },
  order_date: { ...CALENDAR, level_no: 3 },
};

/** The hierarchy level a catalog field sits on, as `/fields` and each filter row send it; null for a field in none. */
export const findMockFieldHierarchy = (fieldId: string): FieldHierarchyRefApi | null =>
  HIERARCHY_BY_FIELD_ID[fieldId] ?? null;

/** The field behind a ref, or undefined when the catalog has no such field of that kind. */
export const findCatalogField = (ref: FieldRefApi): MockCatalogField | undefined => {
  for (const [category, fields] of Object.entries(MOCK_DATA_MODEL_FIELDS_RESPONSE)) {
    const hit = fields.find((field) => field.id === ref.model_field_id && field.kind === ref.kind);
    if (hit) {
      const typed = category as DataModelFieldCategory;
      return { id: hit.id, name: hit.name, kind: hit.kind, category: typed, valueType: MOCK_VALUE_TYPE_BY_CATEGORY[typed] };
    }
  }
  return undefined;
};


const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

const isScalar = (value: unknown): value is FilterScalarApi =>
  value === null || ["string", "number", "boolean"].includes(typeof value);

/** Dates are UTC `YYYY-MM-DD`; the mock buckets to the month start, the grain its seeded data uses. */
export const toPeriodStart = (value: string): string | null => {
  const match = ISO_DATE.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  const valid =
    date.getUTCFullYear() === Number(year) &&
    date.getUTCMonth() === Number(month) - 1 &&
    date.getUTCDate() === Number(day);
  return valid ? `${year}-${month}-01` : null;
};

/**
 * Checks `comparator` against the backend's table for the field's type
 * (`storyFilterComparators`, the same copy the operator menu reads) and
 * `value` against the comparator's shape, then returns the value as stored:
 * dates bucketed, lists de-duplicated.
 */
export const validateFilterValue = (
  field: MockCatalogField,
  comparator: FilterComparator,
  value: unknown,
): FilterScalarApi | FilterScalarApi[] => {
  if (comparator === "relative_date") {
    // As the API: a rolling period on a date, stored as sent (the read type models only scalars).
    if (field.valueType !== "temporal" || typeof value !== "object" || value === null || Array.isArray(value)) {
      throw mockValidationError("relative_date takes a period on a date field");
    }
    return value as unknown as FilterScalarApi;
  }
  const entry = storyFilterComparators[field.valueType].find((candidate) => candidate.comparator === comparator);
  if (!entry || (entry.dimensionOnly && field.kind === "measure")) {
    const target = field.kind === "measure" ? "measure" : "field";
    throw mockValidationError(`${comparator} is not valid for a ${field.valueType} ${target}`);
  }

  switch (entry.valueShape) {
    case "none":
      if (value !== null) throw mockValidationError(`${comparator} takes no value`);
      return null;
    case "list": {
      if (!Array.isArray(value) || value.length === 0 || value.length > storyFilterListLimit || !value.every(isScalar)) {
        throw mockValidationError(`${comparator} needs a list of 1 to ${storyFilterListLimit} values`);
      }
      return Array.from(new Set(bucket(field, value)));
    }
    case "pair": {
      if (!Array.isArray(value) || value.length !== 2 || !value.every(isScalar)) {
        throw mockValidationError("between needs exactly two values");
      }
      const [low, high] = bucket(field, value);
      if (isScalarAfter(low, high)) throw mockValidationError("between needs low <= high");
      return [low, high];
    }
    default:
      if (Array.isArray(value) || !isScalar(value)) {
        throw mockValidationError(`${comparator} needs a single value`);
      }
      if (comparator === "contains" && (typeof value !== "string" || value.trim() === "")) {
        throw mockValidationError("contains needs some text");
      }
      return bucket(field, [value])[0];
  }
};

const bucket = (field: MockCatalogField, values: FilterScalarApi[]): FilterScalarApi[] =>
  field.valueType !== "temporal"
    ? values
    : values.map((value) => {
        const start = typeof value === "string" ? toPeriodStart(value) : null;
        if (start === null) throw mockValidationError(`${String(value)} is not a UTC YYYY-MM-DD date`);
        return start;
      });
